import type { QuoteLine } from './types';

/**
 * Owner 2026-10-23 18:34 („czy mozna przesuwac pozycje gora dol miedzy soba?”): the order of a section's lines.
 * 18:36 („nie chce to nielogiczne”): only within the section; 2026-10-24 10:47 reversed it — see `CrossMove` below.
 */

/** The ids with `id` one place up (-1) or down (1); null at the section's edge. */
export function moveBy(ids: readonly string[], id: string, delta: -1 | 1): string[] | null {
  const at = ids.indexOf(id);
  const to = at + delta;
  if (at < 0 || to < 0 || to >= ids.length) return null;
  const next = [...ids];
  next.splice(at, 1);
  next.splice(to, 0, id);
  return next;
}

/** The ids with `id` dropped before (or `after`) `target`; null when nothing changes. */
export function dropOn(ids: readonly string[], id: string, target: string, after: boolean): string[] | null {
  if (id === target || !ids.includes(id) || !ids.includes(target)) return null;
  const rest = ids.filter((x) => x !== id);
  rest.splice(rest.indexOf(target) + (after ? 1 : 0), 0, id);
  return rest.join() === ids.join() ? null : rest;
}

/** The ids with `id` (put back) at `index`. */
export function putAt(ids: readonly string[], id: string, index: number): string[] {
  const rest = ids.filter((x) => x !== id);
  rest.splice(Math.max(0, Math.min(index, rest.length)), 0, id);
  return rest;
}

/**
 * Owner 2026-10-24 10:47 („pozycje jednak musze moc przenosic miedzy sekcjami … ale jesli zmieniam to pytaj czy
 * napewno”): a line (or the selected lines) dragged into ANOTHER equipment section, after a question. „Obsługa” and
 * „Transport” keep their lines and take none (no drop target there). Saved by the line's PATCH (`sectionId`, #123) with
 * `beforeLineId`: the line it lands before, or null for the section's end.
 */
export type CrossMove = { lineIds: string[]; sectionId: string; beforeLineId: string | null };

/** Where a drop on `target` (before it, or `after`) of `lines` lands: the line it goes before (null: the end). */
export function landing(lines: readonly string[], target: string | null, after: boolean): string | null {
  if (target === null) return null;
  const at = lines.indexOf(target);
  if (at < 0) return null;
  return after ? (lines[at + 1] ?? null) : target;
}

/**
 * ⌥↑ / ⌥↓ at the section's edge: the move into the neighbouring equipment section (⌥↓ to its top, ⌥↑ to its end),
 * or null (not at the edge, or no equipment section there).
 */
export function edgeMove(sections: ReadonlyArray<{ id: string; kind: string; lines: ReadonlyArray<{ id: string }> }>, lineId: string, delta: -1 | 1): CrossMove | null {
  const equipment = sections.filter((x) => x.kind === 'equipment');
  const from = equipment.findIndex((x) => x.lines.some((l) => l.id === lineId));
  const sec = equipment[from];
  if (!sec) return null;
  const at = sec.lines.findIndex((l) => l.id === lineId);
  if (delta === -1 ? at !== 0 : at !== sec.lines.length - 1) return null;
  const to = equipment[from + delta];
  if (!to) return null;
  return { lineIds: [lineId], sectionId: to.id, beforeLineId: delta === 1 ? (to.lines[0]?.id ?? null) : null };
}

/**
 * Owner 2026-10-23 20:46 („chce moc przenosic cale sekcje … razem ze wszystkimi pozycjami”): whole equipment sections
 * move among themselves; "Obsługa" and "Transport" keep their place under them. `moveBy` / `dropOn` work on their ids.
 */

/** The sections with the equipment ones in `ids`' order (one not in `ids`, added since, after them); the fixed last. */
export function withSectionOrder<S extends { id: string; kind: string }>(sections: readonly S[], ids: readonly string[] | null): readonly S[] {
  if (!ids) return sections;
  const equipment = sections.filter((x) => x.kind === 'equipment');
  const byId = new Map(equipment.map((x) => [x.id, x]));
  const asked = ids.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : []));
  return [...asked, ...equipment.filter((x) => !ids.includes(x.id)), ...sections.filter((x) => x.kind !== 'equipment')];
}

/** The body of `PUT …/sections/order`: every equipment section of `fresh` in `ids`' order, with its sort; null when they differ. */
export function sectionOrderBody(fresh: ReadonlyArray<{ id: string; kind: string; sort: number }>, ids: readonly string[]): { sections: Array<{ id: string; sort: number }> } | null {
  const byId = new Map(fresh.filter((x) => x.kind === 'equipment').map((x) => [x.id, x]));
  if (ids.length !== byId.size || new Set(ids).size !== ids.length || ids.some((id) => !byId.has(id))) return null;
  return { sections: ids.map((id) => ({ id, sort: byId.get(id)!.sort })) };
}

/**
 * „Cofnij” of a deleted line: the same line added again through `POST /api/quotes/versions/:id/lines` — its kind and
 * source, name, amounts and description (only the fields the role sees: hidden ones are absent from `line`).
 */
export function restoreBody(line: QuoteLine): Record<string, unknown> {
  const body: Record<string, unknown> = {
    type: line.type,
    sectionId: line.sectionId,
    name: line.name,
    quantity: line.quantity,
    daysMilli: line.daysMilli,
    linear: line.linear,
    description: line.description,
    day: line.day,
  };
  if (line.type === 'item' && line.itemId) body.itemId = line.itemId;
  if (line.type === 'package' && line.packageId) body.packageId = line.packageId;
  if (line.type === 'service' && line.serviceId) body.serviceId = line.serviceId;
  if (line.unitPriceGrosze !== undefined) body.unitPriceGrosze = line.unitPriceGrosze;
  if (line.discountBp !== undefined) body.discountBp = line.discountBp;
  return body;
}

/** A supplier's line takes its rental with it: adding it again would not bring the rental back — no „Cofnij”. */
export const undoable = (line: QuoteLine): boolean => !line.supplierId;
